import { QuestionReference } from '../api/bingliApi'
import { hashString } from '../../helpers/hash'
import { Field, Form, Group, Subform, TranslationTable } from '@icure/form/components/model'

export const MISTRAL_BASE_URL = process.env.REACT_APP_MISTRAL_URL || 'https://preventi-mistral.icure.cloud'

export interface MistralResult {
  text: string
  score: number | undefined
}

const scorePattern = /(.*?)\*{0,2}\s*(Score global|Globale score)\s*:\s*(\d+)\s*\/\s*10[\s*]*(?:\(.+\))?[\s*]*$/

export function parseScoreLine(raw: string): MistralResult {
  const lines = raw.split('\n')
  let score: number | undefined = undefined
  const textLines: string[] = []
  for (const line of lines) {
    const m = line.match(scorePattern)
    if (m) {
      score = parseInt(m[3], 10)
      textLines.push(m[1].trim())
    } else {
      textLines.push(line)
    }
  }
  return { text: textLines.join('\n').trim(), score }
}

export const mistralSummarizeLog = {} as Record<string, { subject: string; status?: string; questions: (string | { question: string; answer?: string })[]; language: string }>

const cache: Record<string, Promise<MistralResult>> = {}
const fieldIndexCache: Record<string, FormFieldIndex> = {}

let questionsCodexPromise: Promise<Record<string, { labels: Record<string, string>; options: [string, Record<string, string>][] }>> | undefined = undefined

const questionsCodex = () =>
  questionsCodexPromise ??
  (questionsCodexPromise = fetch('/form/questions.json')
    .then((response) => response.json())
    .then((codex: Record<string, QuestionReference>) =>
      Object.entries(codex).reduce(
        (acc, [, qr]) => {
          qr.fields?.forEach((field) => {
            acc[field] = acc[field] ?? { labels: qr.label, options: [] }
            acc[field].options = (acc[field].options ?? []).concat(Object.entries(qr.options ?? {}).map(([, v]) => [v.code, v]))
          })
          return acc
        },
        {} as Record<string, { labels: Record<string, string>; options: [string, Record<string, string>][] }>,
      ),
    ))

interface FormFieldIndex {
  [fieldCode: string]: {
    shortLabel: string
    options?: { [optionCode: string]: string }
  }
}

function collectFields(items: Array<Field | Group | Subform>): Field[] {
  const result: Field[] = []
  for (const item of items) {
    if (item.clazz === 'field') {
      result.push(item as Field)
    } else if (item.clazz === 'group') {
      const group = item as Group
      if (group.fields) {
        result.push(...collectFields(group.fields))
      }
    }
  }
  return result
}

function buildFormFieldIndex(form: Form): FormFieldIndex {
  const index: FormFieldIndex = {}
  for (const section of form.sections ?? []) {
    for (const field of collectFields(section.fields)) {
      if (field.field && field.shortLabel) {
        index[field.field] = {
          shortLabel: field.shortLabel,
          options: field.options as { [optionCode: string]: string } | undefined,
        }
      }
    }
  }
  return index
}

function translateKey(translations: TranslationTable[], language: string, key: string): string | undefined {
  const table = translations.find((tt) => tt.language === language) ?? translations.find((tt) => tt.language === 'fr')
  return table?.translations[key]
}

function resolveWithFormTemplate(
  question: string,
  answerOrCode: string,
  language: string,
  fieldIndex: FormFieldIndex,
  translations: TranslationTable[],
): { question: string; answer: string } | undefined {
  const fieldDef = fieldIndex[question]
  if (!fieldDef) return undefined
  const qLabel = translateKey(translations, language, fieldDef.shortLabel)
  if (!qLabel) return undefined
  let answer = answerOrCode
  if (fieldDef.options) {
    const optionTranslationKey = fieldDef.options[answerOrCode]
    if (optionTranslationKey) {
      const translated = translateKey(translations, language, optionTranslationKey as string)
      if (translated) answer = translated
    }
  }
  return { question: qLabel, answer }
}

export type MistralQuestionOption = 'do-not-translate'

export interface MistralSummarizeParams {
  domain: string
  subject: string
  status?: string
  questions: ([string, string] | [string, string, MistralQuestionOption] | string)[]
  language: string
  globalStatus?: string
  domainStatuses?: Record<string, string>
  formTemplate?: Form
}

export const mistralSummarize = async ({
  domain,
  subject,
  status,
  questions,
  language,
  globalStatus,
  domainStatuses,
  formTemplate,
}: MistralSummarizeParams): Promise<MistralResult> => {
  const key = await hashString(`${domain}:${status}:${subject}:${questions.flatMap((x) => x).join('')}`)
  const cached = cache[key]

  const useFormTranslations = formTemplate?.translations?.length
  const templateId = formTemplate?.id
  const fieldIndex = useFormTranslations
    ? templateId && fieldIndexCache[templateId]
      ? fieldIndexCache[templateId]
      : (() => {
          const idx = buildFormFieldIndex(formTemplate)
          if (templateId) fieldIndexCache[templateId] = idx
          return idx
        })()
    : undefined

  const formattedQuestions = await Promise.all(
    questions.map(async (entry) => {
      if (typeof entry === 'string') {
        return { question: entry }
      }
      const [question, answer, option] = entry
      if (option === 'do-not-translate') {
        return { question, answer: answer ?? '' }
      }
      if (useFormTranslations && fieldIndex) {
        return resolveWithFormTemplate(question, answer ?? '', language, fieldIndex, formTemplate.translations!)
      }
      const codex = await questionsCodex()
      const cq = codex[question]
      if (!cq) {
        return { question, answer: answer ?? '' }
      }
      const qLabel = cq.labels[language] ?? cq.labels['fr']
      const qAnswers = cq.options
        ?.filter(([k]) => k === answer)
        .map(([, v]) => v[language])
        .filter((x) => !!x)
      return { question: qLabel, answer: qAnswers?.length ? qAnswers?.join(', ') : answer }
    }),
  )
    .then((q) => q.filter((x): x is { question: string; answer?: string } => !!x))
    .then((questions) => {
      //Used for reporting
      mistralSummarizeLog[domain] = { subject, status, questions: questions, language }
      return questions
    })
  return cached
    ? await cached
    : await (cache[key] = fetch(`${MISTRAL_BASE_URL}/${domain}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject,
          status,
          language,
          globalStatus,
          domainStatuses,
          questions: formattedQuestions,
        }),
      })
        .then((res) => res.text())
        .then(parseScoreLine))
}
